import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { map, mergeMap, switchMap, toArray } from 'rxjs/operators';
import { UserService } from 'src/app/services/user-service/userservice';
import {
  API_N8N_SAAS_CONTAINER_ACTION,
  API_N8N_SAAS_ROUTE_UPSERT,
  API_N8N_SAAS_STACK_ACTION,
  API_N8N_SAAS_STACK_CONTAINERS,
  API_N8N_SAAS_STACKS
} from 'src/environments/api';

export type SaasInstanceStatus = 'ACTIVE' | 'STOPPED' | 'UNKNOWN';
export type SaasContainerAction = 'start' | 'stop';
export type SaasStackAction = 'start' | 'delete';

export interface SaasStackCreateRequest {
  participantName: string;
  pb40Token: string;
}

export interface SaasRouteUpsertRequest {
  customerId: string;
  backendIp: string;
  backendPort: number;
  pb40Token: string;
}

export interface SaasStack {
  id: string;
  name: string;
}

export interface SaasInstance {
  id: string;
  organisation: string | null;
  stack: string;
  serviceType: string;
  serviceTypeLabel: string;
  containerName: string;
  containerId: string;
  hostPort: string | null;
  backendIp: string | null;
  backendPort: number | null;
  baseUrl: string | null;
  createdAt: string | null;
  status: SaasInstanceStatus;
  statusLabel: string;
  dockerStatus: string;
}

interface N8nStackContainers {
  stack: string;
  containers: unknown[];
}

const SAFE_STACK_NAME = /^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,127}$/;
const SAFE_CONTAINER_ID = /^[a-fA-F0-9]{12,64}$/;
const SAFE_BACKEND_IP = /^(?:\d{1,3}\.){3}\d{1,3}$/;
const SAFE_CUSTOMER_ID = /^[a-z0-9][a-z0-9-]{0,62}$/;

@Injectable({ providedIn: 'root' })
export class SaasInstanceDiscoveryService {
  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService
  ) { }

  discoverStacks(): Observable<SaasStack[]> {
    if (!this.userService.isCurrentUserPlatformAdmin()) {
      return throwError(() => new Error('Nur Platform Admins dürfen SaaS-Stacks abrufen.'));
    }

    return this.http.get<unknown>(API_N8N_SAAS_STACKS).pipe(
      map(payload => this.readStacks(payload)),
      map(stacks => stacks.map(name => ({ id: name, name })))
    );
  }

  discoverContainers(stack: string): Observable<SaasInstance[]> {
    if (!this.userService.isCurrentUserPlatformAdmin()) {
      return throwError(() => new Error('Nur Platform Admins dürfen SaaS-Container abrufen.'));
    }

    return this.readStackContainers(stack).pipe(
      map(response => response.containers
        .map(container => this.toInstance(response.stack, container))
        .sort((left, right) => left.containerName.localeCompare(right.containerName)))
    );
  }

  actionContainer(action: SaasContainerAction, containerId: string, pb40Token: string): Observable<unknown> {
    if (!this.userService.isCurrentUserPlatformAdmin()) {
      return throwError(() => new Error('Nur Platform Admins dürfen SaaS-Container steuern.'));
    }
    if (action !== 'start' && action !== 'stop') {
      return throwError(() => new Error(`Ungültige Container-Aktion: ${action}`));
    }
    if (!SAFE_CONTAINER_ID.test(containerId)) {
      return throwError(() => new Error(`Ungültige Container-ID: ${containerId}`));
    }
    const token = pb40Token.trim();
    if (!token) {
      return throwError(() => new Error('Der PB40-Token darf nicht leer sein.'));
    }

    return this.http.post<unknown>(
      API_N8N_SAAS_CONTAINER_ACTION,
      { action, containerId },
      { headers: new HttpHeaders({ 'x-pb40-token': token }) }
    );
  }

  createStack(request: SaasStackCreateRequest): Observable<unknown> {
    return this.actionStack('start', request.participantName, request.pb40Token);
  }

  deleteStack(participantName: string, pb40Token: string): Observable<unknown> {
    return this.actionStack('delete', participantName, pb40Token);
  }

  private actionStack(action: SaasStackAction, participantNameValue: string, pb40Token: string): Observable<unknown> {
    if (!this.userService.isCurrentUserPlatformAdmin()) {
      return throwError(() => new Error('Nur Platform Admins dürfen SaaS-Stacks verwalten.'));
    }

    const participantName = participantNameValue.trim();
    const token = pb40Token.trim();
    if (!SAFE_STACK_NAME.test(participantName)) {
      return throwError(() => new Error(`Ungültiger Docker-Stackname: ${participantName}`));
    }
    if (!token) {
      return throwError(() => new Error('Der PB40-Token darf nicht leer sein.'));
    }

    return this.http.post<unknown>(
      API_N8N_SAAS_STACK_ACTION,
      { action, participantName },
      { headers: new HttpHeaders({ 'x-pb40-token': token }) }
    );
  }

  upsertRoute(request: SaasRouteUpsertRequest): Observable<unknown> {
    if (!this.userService.isCurrentUserPlatformAdmin()) {
      return throwError(() => new Error('Nur Platform Admins dürfen SaaS-Routen verwalten.'));
    }

    const customerId = request.customerId.trim();
    const backendIp = request.backendIp.trim();
    const token = request.pb40Token.trim();
    if (!SAFE_CUSTOMER_ID.test(customerId)) {
      return throwError(() => new Error(`Ungültige Customer-ID/Subdomain: ${customerId}`));
    }
    if (!this.isValidIpv4(backendIp)) {
      return throwError(() => new Error('Die Backend-IP ist ungültig.'));
    }
    if (!Number.isInteger(request.backendPort) || request.backendPort < 1 || request.backendPort > 65535) {
      return throwError(() => new Error('Der Backend-Port muss zwischen 1 und 65535 liegen.'));
    }
    if (!token) {
      return throwError(() => new Error('Der PB40-Token darf nicht leer sein.'));
    }

    return this.http.post<unknown>(
      API_N8N_SAAS_ROUTE_UPSERT,
      {
        action: 'upsert',
        customer_id: customerId,
        backend_ip: backendIp,
        backend_port: request.backendPort
      },
      { headers: new HttpHeaders({ 'x-pb40-token': token }) }
    );
  }

  /** Kept for callers that need a complete flattened discovery result. */
  discoverInstances(): Observable<SaasInstance[]> {
    return this.discoverStacks().pipe(
      switchMap(stacks => {
        if (stacks.length === 0) return of([]);
        return from(stacks).pipe(
          mergeMap(stack => this.discoverContainers(stack.name), 4),
          toArray(),
          map(responses => responses
            .reduce<SaasInstance[]>((instances, containers) => [...instances, ...containers], [])
            .sort((left, right) => left.stack.localeCompare(right.stack) || left.containerName.localeCompare(right.containerName)))
        );
      })
    );
  }

  private readStackContainers(stack: string): Observable<N8nStackContainers> {
    if (!SAFE_STACK_NAME.test(stack)) {
      return throwError(() => new Error(`Ungültiger Docker-Stackname: ${stack}`));
    }

    return this.http.post<unknown>(API_N8N_SAAS_STACK_CONTAINERS, { stack }).pipe(
      map(payload => {
        const body = this.firstRecord(payload);
        if (!body) {
          throw new Error(`Die Container-Antwort für Stack "${stack}" ist ungültig.`);
        }

        const containers = this.readContainers(body);
        if (!containers) {
          throw new Error(`Die Container-Antwort für Stack "${stack}" ist ungültig.`);
        }

        const responseStack = this.text(this.record(containers[0])?.['stack']);
        return {
          stack: this.text(body['stack']) || responseStack || stack,
          containers
        };
      })
    );
  }

  private readContainers(body: Record<string, unknown>): unknown[] | null {
    if (Array.isArray(body['containers'])) return body['containers'];
    if (!Array.isArray(body['result'])) return null;

    return body['result']
      .map(entry => {
        const result = this.record(entry);
        return result?.['json'] ?? entry;
      })
      .filter(entry => this.record(entry) !== null);
  }

  private readStacks(payload: unknown): string[] {
    const body = this.firstRecord(payload);
    const rawStacks = body?.['stacks'];
    if (!Array.isArray(rawStacks)) {
      throw new Error('Die n8n-Antwort enthält keine Stackliste.');
    }

    return [...new Set(rawStacks
      .map(entry => typeof entry === 'string' ? entry : this.text(this.record(entry)?.['stack']))
      .map(stack => stack.trim())
      .filter(Boolean))];
  }

  private toInstance(stack: string, value: unknown): SaasInstance {
    const container = this.record(value) ?? {};
    const containerId = this.text(container['id']) || this.text(container['containerId']);
    const containerName = this.text(container['name']) || this.text(container['containerName']) || containerId || 'Unbenannter Container';
    const dockerStatus = this.text(container['dockerStatus']) || this.text(container['status']) || this.text(container['state']);
    const serviceType = this.text(container['serviceType']) || this.inferServiceType(containerName);
    const status = this.normaliseStatus(dockerStatus);
    const baseUrl = this.text(container['baseUrl']) || this.text(container['url']) || null;
    const hostPort = this.text(container['hostPort']) || null;
    const backendPortValue = this.text(container['backendPort']) || this.text(container['backend_port']) || hostPort;

    return {
      id: `${stack}:${containerId || containerName}`,
      organisation: this.text(container['organisation']) || this.text(container['organization']) || null,
      stack,
      serviceType,
      serviceTypeLabel: this.serviceTypeLabel(serviceType),
      containerName,
      containerId: containerId || '—',
      hostPort,
      backendIp: this.text(container['backendIp']) || this.text(container['backend_ip']) || this.ipv4FromUrl(baseUrl),
      backendPort: this.port(backendPortValue),
      baseUrl,
      createdAt: this.text(container['createdAt']) || this.text(container['creationDate']) || null,
      status,
      statusLabel: this.statusLabel(status),
      dockerStatus: dockerStatus || 'Nicht geliefert'
    };
  }

  private inferServiceType(containerName: string): string {
    const name = containerName.toLowerCase();
    if (name.includes('wallet')) return 'WALLET';
    if (name.includes('connector') || name.includes('controlplane') || name.includes('dataplane')) return 'CONNECTOR';
    if (name.includes('identityhub') || name.includes('identity-hub')) return 'IDENTITY_HUB';
    if (name.includes('issuer')) return 'ISSUER';
    if (name.includes('proxy')) return 'PROXY';
    if (name.includes('vault') || name.includes('openbao')) return 'KEYSTORE';
    if (name.includes('postgres') || name.includes('database') || name.includes('db')) return 'DATABASE';
    return 'OTHER';
  }

  private serviceTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      WALLET: 'Wallet',
      CONNECTOR: 'Konnektor',
      IDENTITY_HUB: 'Identity Hub',
      ISSUER: 'Issuer',
      KEYSTORE: 'Keystore',
      DATABASE: 'Datenbank',
      PROXY: 'Proxy',
      OTHER: 'Sonstiger Dienst'
    };
    return labels[type.toUpperCase()] ?? type;
  }

  private normaliseStatus(value: string): SaasInstanceStatus {
    const status = value.toLowerCase();
    if (/\b(up|running|healthy)\b/.test(status)) return 'ACTIVE';
    if (/\b(exited|stopped|dead|created)\b/.test(status)) return 'STOPPED';
    return 'UNKNOWN';
  }

  private statusLabel(status: SaasInstanceStatus): string {
    if (status === 'ACTIVE') return 'Aktiv';
    if (status === 'STOPPED') return 'Gestoppt';
    return 'Unbekannt';
  }

  private ipv4FromUrl(value: string | null): string | null {
    if (!value) return null;
    try {
      const url = new URL(/^https?:\/\//i.test(value) ? value : `http://${value}`);
      return this.isValidIpv4(url.hostname) ? url.hostname : null;
    } catch {
      return null;
    }
  }

  private port(value: string): number | null {
    if (!/^\d+$/.test(value)) return null;
    const port = Number(value);
    return Number.isInteger(port) && port >= 1 && port <= 65535 ? port : null;
  }

  private isValidIpv4(value: string): boolean {
    if (!SAFE_BACKEND_IP.test(value)) return false;
    return value.split('.').every(part => {
      const octet = Number(part);
      return Number.isInteger(octet) && octet >= 0 && octet <= 255;
    });
  }

  private firstRecord(payload: unknown): Record<string, unknown> | null {
    if (Array.isArray(payload)) return this.record(payload[0]);
    return this.record(payload);
  }

  private record(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : null;
  }

  private text(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  }
}
